import { useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import { QuoteFormProvider } from "./components/QuoteFormContext";
import PremiumLoader from "./components/PremiumLoader";
import VisitPopup from "./components/VisitPopup";
import DeferredScripts from "./components/DeferredScripts";

// The home route is imported eagerly so its LCP is not delayed by an extra chunk
// round-trip. Every other route is code-split and loaded on demand, which keeps
// their JS out of the initial download (reduces unused JavaScript site-wide).
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  const [loaderDone, setLoaderDone] = useState(false);
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <QuoteFormProvider>
        <PremiumLoader onDone={() => setLoaderDone(true)} />
        <VisitPopup ready={loaderDone} />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <ChunkErrorBoundary>
            <Suspense fallback={null}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/about" element={<About />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/locations/:slug" element={<LocationDetail />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ChunkErrorBoundary>
        </BrowserRouter>
        <DeferredScripts />
      </QuoteFormProvider>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
